/**
 * Created by piotr@revisual.io on 25/07/2025.
 */
import { hasSubscription } from "../../../helpers/organization";
import { useCurrentOrganizationStore } from "../../../../../hooks/redux/currentOrganization";
import QuotaItem, {
  QuotaFooterItem,
  QuotaStripItem,
  QuotaStripFooter,
} from "./QuotaItem";
import { UsageFieldsMapping } from "../../../../../consts";
import { Link } from "react-router-dom";
import { orderQuotas } from "../../../helpers/subscription";
import Card from "../../../theme/untitled/Card/Card";
import { MONTHLY_RESET_QUOTAS, getNextMonthlyReset } from "./utils";
import "./SubscriptionLimits.css";

/**
 * Same data, same figures, same thresholds in both layouts — only the
 * arrangement differs. `layout` defaults to "vertical" so every existing
 * call site (the billing page) keeps today's rendering unedited.
 *
 * `title`/`splitFooter` let design_handoff_billing's Card 2 reuse this
 * component's rows/thresholds verbatim while matching its own header copy
 * ("Plan usage" / "What you are using under the {plan}.") and moving the
 * reset date into a real Card.Footer instead of the subtitle sentence —
 * without touching the default rendering every other call site relies on.
 *
 * @param {boolean} showManageButton
 * @param {"vertical"|"horizontal"} layout
 * @param {string} title
 * @param {boolean} splitFooter
 * @returns {JSX.Element}
 * @constructor
 */
const SubscriptionLimits = ({
  showManageButton = true,
  layout = "vertical",
  title = "Quota summary",
  splitFooter = false,
}) => {
  const { currentOrganization } = useCurrentOrganizationStore();
  const subscription = hasSubscription(currentOrganization);

  const quotas = subscription.quotas;

  if (!quotas) {
    return (
      <div className="alert alert-warning">
        No subscription limits available.
      </div>
    );
  }

  const usage = currentOrganization.model.usage;
  // sorting order is following: quota with value > 0 first, then quota with value < 0, then quota with value = 0
  const orderedKeys = orderQuotas(quotas, usage);
  // Tracked quotas (have a usage field) get an `x of y` row + bar; the rest
  // (Synced events, Future events pulled) are untracked and go in the footer.
  const trackedKeys = orderedKeys.filter((key) => UsageFieldsMapping[key]);
  const footerKeys = orderedKeys.filter((key) => !UsageFieldsMapping[key]);

  const isHorizontal = layout === "horizontal";
  const ItemComponent = isHorizontal ? QuotaStripItem : QuotaItem;

  const rows = trackedKeys.map((key) => (
    <ItemComponent
      key={key}
      quotaKey={key}
      quota={Number(quotas[key])}
      usage={Number(usage[UsageFieldsMapping[key]])}
      resetsMonthly={MONTHLY_RESET_QUOTAS.has(key)}
    />
  ));

  // Vertical mode renders one <dl> row per untracked quota; horizontal
  // collapses both into a single footer sentence — see QuotaStripFooter.
  const footerItems = footerKeys.map((key) => ({
    quotaKey: key,
    quota: Number(quotas[key]),
    resetsMonthly: MONTHLY_RESET_QUOTAS.has(key),
  }));
  const footerRows = footerKeys.map((key) => (
    <QuotaFooterItem
      key={key}
      quotaKey={key}
      quota={Number(quotas[key])}
      resetsMonthly={MONTHLY_RESET_QUOTAS.has(key)}
    />
  ));

  return (
    <Card>
      <Card.Header
        className="qs-header"
        title={title}
        subtitle={
          splitFooter ? (
            <>
              What you are using under the{" "}
              <strong className="qs-emphasis">{subscription.name}</strong>.
            </>
          ) : (
            <>
              Your usage under the{" "}
              <strong className="qs-emphasis">{subscription.name}</strong>{" "}
              plan. Monthly limits reset on{" "}
              <strong className="qs-emphasis">{getNextMonthlyReset()}</strong>
            </>
          )
        }
      >
        <div className="qs-actions">
          <Link to="/reports/plan-usage" className="qs-btn qs-btn--tertiary">
            View plan usage
          </Link>
          {showManageButton && (
            <Link to="/billing" className="qs-btn qs-btn--secondary">
              Manage plan
            </Link>
          )}
        </div>
      </Card.Header>
      <Card.Body noPadding>
        {isHorizontal ? (
          <>
            <div className="qs-strip">{rows}</div>
            {footerItems.length > 0 && <QuotaStripFooter items={footerItems} />}
          </>
        ) : (
          <>
            <div className="qs-rows">{rows}</div>
            {footerRows.length > 0 && (
              <div className="qs-footer">
                <dl className="qs-footer-list">{footerRows}</dl>
              </div>
            )}
          </>
        )}
      </Card.Body>
      {splitFooter && (
        <Card.Footer>
          Monthly limits reset on{" "}
          <strong className="qs-emphasis">{getNextMonthlyReset()}</strong>
        </Card.Footer>
      )}
    </Card>
  );
};

export default SubscriptionLimits;
